import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  TextField,
  Divider,
  Button,
  CircularProgress,
  Stack,
  FormGroup,
  Checkbox,
} from '@mui/material';
import { generateContent } from '../../services/AIService';
import {
  assembleRechallengeSection713,
  buildRechallenge713Prompt,
  DEFAULT_RECHALLENGE_DECISIONS,
  RECHALLENGE_AFTER_TEMP_DISC,
  LIVER_RESTART_AFTER_STOPPING,
  RECHALLENGE_REQUIREMENT_OPTIONS,
  RECHALLENGE_NOT_PERMITTED_STATEMENT,
  toLiverRestartLibraryValue,
  fromLiverRestartLibraryValue,
} from '../../utils/rechallengeSection713';

const DECISIONS_PATH = 'discontinuation_rules.rechallenge_decisions';
const OUTPUT_PATH = 'discontinuation_rules.rechallenge';
const LIVER_RESTART_PATH =
  'template_language_fields.liver_patient_library_decisions.restart_rechallenge';

const RechallengeSection713Panel = ({ values, setFieldValue, isFormDisabled }) => {
  const decisions =
    values?.discontinuation_rules?.rechallenge_decisions || DEFAULT_RECHALLENGE_DECISIONS;
  const [generateBusy, setGenerateBusy] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  const setDecision = useCallback(
    (field, value) => {
      setFieldValue(`${DECISIONS_PATH}.${field}`, value);
    },
    [setFieldValue]
  );

  // Seed liver Q2 from existing liver patient library selection when empty
  useEffect(() => {
    if (decisions.liver_restart_after_stopping) return;
    const fromLiver = fromLiverRestartLibraryValue(
      values?.template_language_fields?.liver_patient_library_decisions?.restart_rechallenge
    );
    if (fromLiver) {
      setFieldValue(`${DECISIONS_PATH}.liver_restart_after_stopping`, fromLiver);
    }
  }, [
    decisions.liver_restart_after_stopping,
    values?.template_language_fields?.liver_patient_library_decisions?.restart_rechallenge,
    setFieldValue,
  ]);

  const syncLiverRestart = useCallback(
    (liverValue) => {
      setDecision('liver_restart_after_stopping', liverValue);
      const mapped = toLiverRestartLibraryValue(liverValue);
      if (mapped) {
        setFieldValue(LIVER_RESTART_PATH, mapped);
      }
      if (liverValue === LIVER_RESTART_AFTER_STOPPING.NOT_PERMITTED) {
        setDecision('protocol_requirements', []);
        setDecision('other_requirement_text', '');
      }
    },
    [setDecision, setFieldValue]
  );

  const assemblyInput = useMemo(
    () => ({
      discontinuation_rules: {
        rechallenge_decisions: decisions,
        rechallenge: values?.discontinuation_rules?.rechallenge || '',
      },
    }),
    [
      decisions.rechallenge_after_temporary_discontinuation,
      decisions.liver_restart_after_stopping,
      decisions.protocol_requirements,
      decisions.other_requirement_text,
      decisions.protocol_source_excerpts,
      values?.discontinuation_rules?.rechallenge,
    ]
  );

  const assembly = useMemo(() => assembleRechallengeSection713(assemblyInput), [assemblyInput]);

  // Sync deterministic No pathway into output field
  useEffect(() => {
    if (decisions.rechallenge_after_temporary_discontinuation !== RECHALLENGE_AFTER_TEMP_DISC.NO) {
      return;
    }
    if (values?.discontinuation_rules?.rechallenge !== RECHALLENGE_NOT_PERMITTED_STATEMENT) {
      setFieldValue(OUTPUT_PATH, RECHALLENGE_NOT_PERMITTED_STATEMENT);
    }
  }, [
    decisions.rechallenge_after_temporary_discontinuation,
    setFieldValue,
    values?.discontinuation_rules?.rechallenge,
  ]);

  const toggleRequirement = (id) => {
    const current = Array.isArray(decisions.protocol_requirements)
      ? [...decisions.protocol_requirements]
      : [];
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    setDecision('protocol_requirements', next);
    if (id === 'other' && current.includes(id)) {
      setDecision('other_requirement_text', '');
    }
  };

  const handleGenerate = async () => {
    const excerpts = String(decisions.protocol_source_excerpts || '').trim();
    if (!excerpts) {
      setGenerateError('Paste protocol source excerpts first.');
      return;
    }
    setGenerateBusy(true);
    setGenerateError(null);
    try {
      const prompt = buildRechallenge713Prompt({ protocolExcerpts: excerpts, decisions });
      const text = await generateContent(prompt, '', '', 'rechallenge_713', '7.1.3');
      const body = String(text || '')
        .trim()
        .replace(/^7\.1\.3[^\n]*\n+/i, '')
        .trim();
      if (!body) {
        throw new Error('Empty response from generation.');
      }
      setFieldValue(OUTPUT_PATH, body);
    } catch (e) {
      setGenerateError(e.message || 'Could not generate Section 7.1.3 from protocol source.');
    } finally {
      setGenerateBusy(false);
    }
  };

  const showYesPath =
    decisions.rechallenge_after_temporary_discontinuation === RECHALLENGE_AFTER_TEMP_DISC.YES;
  const showRequirements =
    decisions.liver_restart_after_stopping === LIVER_RESTART_AFTER_STOPPING.PERMITTED;
  const outputText = values?.discontinuation_rules?.rechallenge || '';

  return (
    <Box sx={{ mb: 3, p: 2, border: '1px solid #dbe3eb', borderRadius: 1, bgcolor: '#fafbfc' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        Section 7.1.3 — Rechallenge
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        General restart/rechallenge after temporary discontinuation. Liver-specific procedures stay in
        Section 7.1.3.1 (cross-reference only). When Yes, narrative is authored only from protocol
        source excerpts plus your answers — no invented approvals or monitoring.
      </Typography>

      {assembly.warnings.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {assembly.warnings.map((w) => (
            <div key={w}>{w}</div>
          ))}
        </Alert>
      )}
      {generateError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGenerateError(null)}>
          {generateError}
        </Alert>
      )}

      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
          1. Does the protocol permit rechallenge or restart after temporary discontinuation?
        </FormLabel>
        <RadioGroup
          value={decisions.rechallenge_after_temporary_discontinuation || ''}
          onChange={(e) => {
            const next = e.target.value;
            setDecision('rechallenge_after_temporary_discontinuation', next);
            if (next === RECHALLENGE_AFTER_TEMP_DISC.NO) {
              setDecision('protocol_source_excerpts', '');
              setFieldValue(OUTPUT_PATH, RECHALLENGE_NOT_PERMITTED_STATEMENT);
            } else {
              setFieldValue(OUTPUT_PATH, '');
            }
          }}
        >
          <FormControlLabel
            value={RECHALLENGE_AFTER_TEMP_DISC.YES}
            control={<Radio size="small" />}
            label="Yes"
          />
          <FormControlLabel
            value={RECHALLENGE_AFTER_TEMP_DISC.NO}
            control={<Radio size="small" />}
            label="No"
          />
        </RadioGroup>
      </FormControl>

      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={isFormDisabled}>
        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
          2. After liver stopping criteria are met:
        </FormLabel>
        <RadioGroup
          value={decisions.liver_restart_after_stopping || ''}
          onChange={(e) => syncLiverRestart(e.target.value)}
        >
          <FormControlLabel
            value={LIVER_RESTART_AFTER_STOPPING.NOT_PERMITTED}
            control={<Radio size="small" />}
            label="Restart/Rechallenge NOT permitted"
          />
          <FormControlLabel
            value={LIVER_RESTART_AFTER_STOPPING.PERMITTED}
            control={<Radio size="small" />}
            label="Restart/Rechallenge permitted"
          />
        </RadioGroup>
        <Typography variant="caption" color="text.secondary">
          Syncs with the Liver Patient Library (Section 7.1.3.1). Detailed liver text is not written
          here.
        </Typography>
      </FormControl>

      {showRequirements && (
        <Box sx={{ mb: 2 }}>
          <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
            3. Protocol-specific requirements (select all documented)
          </FormLabel>
          <FormGroup>
            {RECHALLENGE_REQUIREMENT_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.id}
                control={
                  <Checkbox
                    size="small"
                    checked={(decisions.protocol_requirements || []).includes(opt.id)}
                    onChange={() => toggleRequirement(opt.id)}
                    disabled={isFormDisabled}
                  />
                }
                label={opt.label}
              />
            ))}
          </FormGroup>
          {(decisions.protocol_requirements || []).includes('other') && (
            <TextField
              fullWidth
              sx={{ mt: 1 }}
              label="Other (Specify)"
              value={decisions.other_requirement_text || ''}
              onChange={(e) => setDecision('other_requirement_text', e.target.value)}
              disabled={isFormDisabled}
            />
          )}
        </Box>
      )}

      {showYesPath && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={6}
            label="Protocol source excerpts (sole clinical source of truth)"
            placeholder="Paste protocol text defining general rechallenge/restart after temporary discontinuation, approvals, re-consent, monitoring, and SoA cross-references. Do not paste detailed liver 7.1.3.1 algorithms here."
            value={decisions.protocol_source_excerpts || ''}
            onChange={(e) => setDecision('protocol_source_excerpts', e.target.value)}
            disabled={isFormDisabled}
            helperText="Only this text plus your answers may be used. Do not invent criteria missing from the source."
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
            <Button
              size="small"
              variant="contained"
              onClick={handleGenerate}
              disabled={
                isFormDisabled ||
                generateBusy ||
                !String(decisions.protocol_source_excerpts || '').trim()
              }
            >
              {generateBusy ? <CircularProgress size={18} /> : 'Generate 7.1.3 from Protocol Source'}
            </Button>
          </Stack>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Assembled Section 7.1.3 preview
      </Typography>
      <Box
        sx={{
          p: 1.5,
          bgcolor: '#fff',
          border: '1px solid #e5eaf0',
          borderRadius: 1,
          whiteSpace: 'pre-wrap',
          fontSize: 13,
          maxHeight: 280,
          overflow: 'auto',
        }}
      >
        {outputText || (
          <Typography variant="body2" color="text.secondary">
            Complete the questions above to assemble Section 7.1.3.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default RechallengeSection713Panel;
